/**
 * Pure, deterministic tree-layout helper for the ownership graph SVG.
 *
 * Coordinate system:
 *   - (0, 0) is top-left of the viewBox
 *   - y increases downward
 *   - Root company (level 0) is placed at the BOTTOM
 *   - Owners (level N) are placed ABOVE (smaller y values)
 *
 * Edge direction in the data: edge.from = owner, edge.to = owned.
 * So "children" in the visual tree are owners of the current node.
 */

import type { GraphNode, GraphEdge, GraphNodeKind } from '../../../types'
import {
  finalizeVerticalViewport,
  tierHeightForSpan,
  type LayoutViewportOptions,
} from './layoutViewport'

export type { LayoutViewportOptions }

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const NODE_W_COMPANY = 164
const NODE_H_COMPANY = 46
const NODE_W_PERSON  = 148
const NODE_H_PERSON  = 38

export const LEVEL_H   = 108  // vertical distance between level top-edges (room for curved edges + labels)
export const NODE_GAP  = 26   // horizontal gap between siblings
export const TARGET_CLUSTER_EPS = 2
export const PADDING_X = 40
export const PADDING_Y = 28
/** Side-step for a middle tier when a shortcut edge skips over it. */
export const INTERMEDIATE_SIDE_OFFSET = 88
/** Flat root-owner branch gap as a fraction of the adjacent column pitch. */
export const FLAT_ROOT_OWNER_BRANCH_GAP_FACTOR = 0.4

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface PositionedNode {
  id:     string
  x:      number  // left edge of node rect
  y:      number  // top edge of node rect
  w:      number
  h:      number
  level:  number
}

export interface LayoutResult {
  positions:  Record<string, PositionedNode>
  svgWidth:   number
  svgHeight:  number
  /** Lowest level index (negative when subsidiaries sit below root). */
  minLevel:   number
  maxLevel:   number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function nodeDims(kind: GraphNodeKind): { w: number; h: number } {
  if (kind === 'person') return { w: NODE_W_PERSON, h: NODE_H_PERSON }
  return { w: NODE_W_COMPANY, h: NODE_H_COMPANY }
}

/** Horizontal centre of a positioned node. */
export function nodeCenterX(p: PositionedNode): number {
  return p.x + p.w / 2
}

/**
 * Pack nodes on one level left→right at their target centres without overlap.
 * Returns left-edge x for each id (same order as `ids`).
 */
function packRowLegacy(
  sorted: string[],
  targetCx: Map<string, number>,
  nodeById: Map<string, GraphNode>,
): Map<string, number> {
  const out = new Map<string, number>()
  let prevRight = -Infinity
  for (const id of sorted) {
    const n = nodeById.get(id)!
    const { w } = nodeDims(n.kind)
    const wantLeft = (targetCx.get(id) ?? PADDING_X) - w / 2
    const minLeft = prevRight === -Infinity
      ? wantLeft
      : prevRight + NODE_GAP
    const x = Math.max(wantLeft, minLeft)
    out.set(id, x)
    prevRight = x + w
  }
  return out
}

export function packRowAtTargets(
  ids: string[],
  targetCx: Map<string, number>,
  nodeById: Map<string, GraphNode>,
  clusterCoOwners = false,
): Map<string, number> {
  if (ids.length === 0) return new Map()

  const sorted = [...ids].sort((a, b) => {
    const da = targetCx.get(a) ?? 0
    const db = targetCx.get(b) ?? 0
    if (da !== db) return da - db
    return a.localeCompare(b)
  })

  if (!clusterCoOwners) {
    return packRowLegacy(sorted, targetCx, nodeById)
  }

  const out = new Map<string, number>()
  const clusters: string[][] = []
  let cluster: string[] = []
  for (const id of sorted) {
    const t = targetCx.get(id) ?? PADDING_X
    if (cluster.length === 0) {
      cluster.push(id)
      continue
    }
    const clusterT = targetCx.get(cluster[0]!) ?? PADDING_X
    if (Math.abs(t - clusterT) <= TARGET_CLUSTER_EPS) {
      cluster.push(id)
    } else {
      clusters.push(cluster)
      cluster = [id]
    }
  }
  if (cluster.length > 0) clusters.push(cluster)

  let prevRight = -Infinity
  for (const group of clusters) {
    const widths = group.map(id => nodeDims(nodeById.get(id)!.kind).w)
    const totalW =
      widths.reduce((sum, w) => sum + w, 0) + (group.length - 1) * NODE_GAP
    const meanTarget =
      group.reduce((sum, id) => sum + (targetCx.get(id) ?? PADDING_X), 0) /
      group.length
    let left = meanTarget - totalW / 2
    if (prevRight !== -Infinity) {
      left = Math.max(left, prevRight + NODE_GAP)
    }
    for (let i = 0; i < group.length; i++) {
      const id = group[i]!
      const w = widths[i]!
      out.set(id, left)
      left += w + NODE_GAP
    }
    prevRight = left - NODE_GAP
  }
  return out
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Compute node positions for a top-down ownership tree.
 *
 * Algorithm:
 * 1. Assign levels by **longest path from root upward**: root = 0; for every
 *    edge `owner → owned`, `level(owner) ≥ level(owned) + 1` (take the max).
 *    Owners always sit strictly above what they own — never on the same row.
 *    Shortcut edges (e.g. JB→B when JB also owns A and A owns B) span
 *    multiple tiers instead of bending sideways on one level.
 * 2. Siblings with no ownership edge between them may still share a level
 *    (e.g. two direct co-owners of the root).
 * 3. **Horizontal placement** — iterative barycentre on real x-coordinates:
 *    each node moves toward the average centre of everything it owns below
 *    and everything that owns it above, then rows are packed without overlap.
 *    Single-node tiers sit under their parent chain instead of dead-centre.
 * 4. Sibling order within a row follows target-x (stable lex tie-break).
 */
/** Map sparse level indices onto consecutive integers (removes empty vertical gaps). */
function compressLevelMap(levels: Map<string, number>): void {
  const uniq = [...new Set(levels.values())].sort((a, b) => a - b)
  const remap = new Map(uniq.map((l, i) => [l, uniq[0] + i]))
  for (const [id, l] of levels) {
    levels.set(id, remap.get(l)!)
  }
}

export function layoutTree(
  nodes:  GraphNode[],
  edges:  GraphEdge[],
  rootId: string,
  viewport?: LayoutViewportOptions,
): LayoutResult {
  // Self-ownership rows in CVR must not affect layout (they inflate levels).
  const layoutEdges = edges.filter(e => e.from !== e.to)

  // owners map: nodeId → [its owners]
  const ownersOf = new Map<string, string[]>()
  nodes.forEach(n => ownersOf.set(n.id, []))
  layoutEdges.forEach(e => {
    const arr = ownersOf.get(e.to) ?? []
    if (!arr.includes(e.from)) arr.push(e.from)
    ownersOf.set(e.to, arr)
  })

  // Longest-path layering from root: every owner is strictly above its owned.
  const levels = new Map<string, number>()
  levels.set(rootId, 0)

  let changed = true
  let guard = 0
  const maxIter = Math.max(nodes.length, 1) + 2
  while (changed && guard < maxIter) {
    changed = false
    guard++
    for (const e of layoutEdges) {
      const toLevel = levels.get(e.to)
      if (toLevel === undefined) continue
      const next = toLevel + 1
      const prev = levels.get(e.from)
      if (prev === undefined || next > prev) {
        levels.set(e.from, next)
        changed = true
      }
    }
  }

  // Subsidiaries below owners: level(owned) <= level(owner) - 1
  changed = true
  guard = 0
  while (changed && guard < maxIter) {
    changed = false
    guard++
    for (const e of layoutEdges) {
      const fromLevel = levels.get(e.from)
      if (fromLevel === undefined) continue
      const next = fromLevel - 1
      const prev = levels.get(e.to)
      if (prev === undefined || next < prev) {
        levels.set(e.to, next)
        changed = true
      }
    }
  }

  // Orphans / nodes not placed by either pass → level 0
  nodes.forEach(n => { if (!levels.has(n.id)) levels.set(n.id, 0) })

  compressLevelMap(levels)

  const maxLevel = nodes.length === 0
    ? 0
    : Math.max(...nodes.map(n => levels.get(n.id) ?? 0))
  const minLevel = nodes.length === 0
    ? 0
    : Math.min(...nodes.map(n => levels.get(n.id) ?? 0))

  // Group nodes by level. Initial order: lex by id (deterministic baseline).
  const byLevel = new Map<number, string[]>()
  for (let l = minLevel; l <= maxLevel; l++) byLevel.set(l, [])
  const sorted = [...nodes].sort((a, b) => a.id.localeCompare(b.id))
  sorted.forEach(n => {
    const l = levels.get(n.id) ?? 0
    byLevel.get(l)!.push(n.id)
  })

  const nodeById = new Map(nodes.map(n => [n.id, n]))

  // Direct graph neighbours for horizontal alignment (any tier gap).
  const ownedBy = new Map<string, string[]>()
  const ownersOfNode = new Map<string, string[]>()
  for (const n of nodes) {
    ownedBy.set(
      n.id,
      layoutEdges.filter(e => e.from === n.id).map(e => e.to),
    )
    ownersOfNode.set(
      n.id,
      layoutEdges.filter(e => e.to === n.id).map(e => e.from),
    )
  }

  // Seed target x: spread by column index on each row.
  const targetCx = new Map<string, number>()
  for (const ids of byLevel.values()) {
    ids.forEach((id, i) => {
      targetCx.set(id, PADDING_X + i * (NODE_W_COMPANY + NODE_GAP) + NODE_W_COMPANY / 2)
    })
  }

  const positions: Record<string, PositionedNode> = {}
  const levelSpan = maxLevel - minLevel
  const tierH = tierHeightForSpan(levelSpan)

  /** True for a simple ownership chain (no owner with multiple holdings). */
  const packOncePerIter = (() => {
    if ((ownersOfNode.get(rootId) ?? []).length !== 1) return false
    for (const n of nodes) {
      const owned = (ownedBy.get(n.id) ?? []).filter(c => c !== rootId)
      if (owned.length > 1) return false
    }
    return true
  })()

  /** True when every node on this level is owned only by the root (typical subsidiary row). */
  function isRootOwnedRow(level: number, ids: string[]): boolean {
    if (level !== minLevel || ids.length === 0) return false
    return ids.every(id => {
      const parents = ownersOfNode.get(id) ?? []
      return parents.length === 1 && parents[0] === rootId
    })
  }

  /** Direct root owner with no upstream and no holdings except root. */
  function isFlatRootOwner(ownerId: string): boolean {
    const owned = (ownedBy.get(ownerId) ?? []).filter(c => c !== rootId)
    if (owned.length > 0) return false
    if ((ownersOfNode.get(ownerId) ?? []).length > 0) return false
    const level = levels.get(ownerId)
    if (level === undefined) return false
    const rowIds = byLevel.get(level) ?? []
    return rowIds.some(id => id !== ownerId && siblingHasSubtree(id))
  }

  /** Sibling on the same row that has an upstream chain and/or downstream holdings. */
  function siblingHasSubtree(id: string): boolean {
    if ((ownedBy.get(id) ?? []).filter(c => c !== rootId).length > 0) return true
    return (ownersOfNode.get(id) ?? []).length > 0
  }

  /** Pull flat root owners onto the row beside the nearest subtree sibling cluster. */
  function subtreeSiblingIdsOnRow(rowIds: string[], flatOwnerId: string): string[] {
    return rowIds
      .filter(id => id !== flatOwnerId && siblingHasSubtree(id))
      .sort((a, b) => nodeCenterX(positions[a]!) - nodeCenterX(positions[b]!))
  }

  /** Center-to-center pitch between adjacent subtree columns on the same row. */
  function subtreeColumnPitch(rowIds: string[], flatOwnerId: string): number {
    const cols = subtreeSiblingIdsOnRow(rowIds, flatOwnerId)
    if (cols.length >= 2) {
      return nodeCenterX(positions[cols[1]!]!) - nodeCenterX(positions[cols[0]!]!)
    }
    return nodeDims('company').w + NODE_GAP
  }

  /** Gap between a flat root owner and the next branch's left edge. */
  function flatRootOwnerBranchGap(rowIds: string[], flatOwnerId: string): number {
    const pitch = subtreeColumnPitch(rowIds, flatOwnerId)
    return Math.max(NODE_GAP, pitch * FLAT_ROOT_OWNER_BRANCH_GAP_FACTOR)
  }

  /**
   * Final pass: gap scaled from the adjacent column pitch so the flat branch
   * reads as its own root branch without sitting in the neighbour's canopy.
   *
   * Multiple flat owners sharing the same foot are staggered right-to-left
   * (lex-sorted, rightmost first) so they don't overlap.
   */
  function finalizeFlatRootOwnerBranchSpacing() {
    type Group = { gap: number; footX: number; ownerIds: string[] }
    const groups = new Map<string, Group>()

    for (const ownerId of ownersOfNode.get(rootId) ?? []) {
      if (ownerId === rootId || !isFlatRootOwner(ownerId)) continue
      const level = levels.get(ownerId)!
      const rowIds = byLevel.get(level) ?? []
      const cols = subtreeSiblingIdsOnRow(rowIds, ownerId)
      if (cols.length === 0) continue
      const gap = flatRootOwnerBranchGap(rowIds, ownerId)
      const foot = positions[cols[0]!]!
      const key = `${level}:${cols[0]!}`
      if (!groups.has(key)) {
        groups.set(key, { gap, footX: foot.x, ownerIds: [] })
      }
      groups.get(key)!.ownerIds.push(ownerId)
    }

    for (const { gap, footX, ownerIds } of groups.values()) {
      // Lex sort for determinism; pack right-to-left so the cluster ends at footX − gap.
      const sorted = [...ownerIds].sort((a, b) => a.localeCompare(b))
      let rightEdge = footX - gap
      for (let i = sorted.length - 1; i >= 0; i--) {
        const flat = positions[sorted[i]!]!
        flat.x = rightEdge - flat.w
        rightEdge = flat.x - NODE_GAP
        positions[sorted[i]!] = flat
      }
    }
  }

  // Direct root owners that are leaves sitting beside a subtree sibling. These
  // are kept OUT of the subtree row during packing so they can never be packed
  // between two subtree columns (which would tear the subtree apart once
  // finalizeFlatRootOwnerBranchSpacing later pulls them to the left). Their
  // precise x is assigned by finalizeFlatRootOwnerBranchSpacing.
  const flatOwnerIds = new Set<string>()
  for (const ownerId of ownersOfNode.get(rootId) ?? []) {
    if (ownerId !== rootId && isFlatRootOwner(ownerId)) flatOwnerIds.add(ownerId)
  }

  function placeAllRows() {
    for (let level = minLevel; level <= maxLevel; level++) {
      const ids = byLevel.get(level)!
      const topY = PADDING_Y + (maxLevel - level) * tierH

      // Pack the subtree (everything except flat owners) so it stays contiguous.
      const flatsOnRow = ids.filter(id => flatOwnerIds.has(id))
      const mainIds = flatsOnRow.length > 0
        ? ids.filter(id => !flatOwnerIds.has(id))
        : ids
      const clusterRow = packOncePerIter || isRootOwnedRow(level, mainIds)
      const xs = packRowAtTargets(mainIds, targetCx, nodeById, clusterRow)

      // Provisionally stack flat owners as a contiguous block to the LEFT of the
      // subtree (final gap set by finalizeFlatRootOwnerBranchSpacing).
      if (flatsOnRow.length > 0 && mainIds.length > 0) {
        const leftEdge = Math.min(...mainIds.map(id => xs.get(id) ?? PADDING_X))
        const sortedFlats = [...flatsOnRow].sort((a, b) => a.localeCompare(b))
        let rightEdge = leftEdge - NODE_GAP
        for (let i = sortedFlats.length - 1; i >= 0; i--) {
          const { w } = nodeDims(nodeById.get(sortedFlats[i]!)!.kind)
          const x = rightEdge - w
          xs.set(sortedFlats[i]!, x)
          rightEdge = x - NODE_GAP
        }
      }

      const sorted = [...ids].sort((a, b) => (xs.get(a) ?? 0) - (xs.get(b) ?? 0))
      byLevel.set(level, sorted)
      for (const id of sorted) {
        const n = nodeById.get(id)!
        const { w, h } = nodeDims(n.kind)
        const yOffset = Math.floor((NODE_H_COMPANY - h) / 2)
        positions[id] = {
          id,
          x: xs.get(id) ?? PADDING_X,
          y: topY + yOffset,
          w,
          h,
          level,
        }
      }
    }
  }

  placeAllRows()

  let layoutMoved = false
  function setTargetFromAnchor(id: string, anchorIds: string[]) {
    if (anchorIds.length === 0) return
    let sum = 0
    let n = 0
    for (const nid of anchorIds) {
      const p = positions[nid]
      if (!p) continue
      sum += nodeCenterX(p)
      n++
    }
    if (n === 0) return
    const cx = sum / n
    const prev = targetCx.get(id) ?? cx
    if (Math.abs(prev - cx) > 0.5) layoutMoved = true
    targetCx.set(id, cx)
  }

  for (let iter = 0; iter < 10; iter++) {
    layoutMoved = false

    // 1) Holdings / companies: sit under their owners (not the root below).
    for (const n of nodes) {
      if (n.kind === 'person') continue
      if (n.id === rootId) continue
      const parents = (ownersOfNode.get(n.id) ?? []).filter(p => p !== rootId)
      setTargetFromAnchor(n.id, parents)
    }
    if (!packOncePerIter) placeAllRows()

    // 2) Persons: sit above what they own.
    for (const n of nodes) {
      if (n.kind !== 'person') continue
      const owned = (ownedBy.get(n.id) ?? []).filter(c => c !== rootId)
      setTargetFromAnchor(n.id, owned)
    }
    if (!packOncePerIter) placeAllRows()

    // 2b) Direct subsidiaries: pack under root (root is excluded from step 1 parents).
    for (const subId of ownedBy.get(rootId) ?? []) {
      if (subId === rootId) continue
      setTargetFromAnchor(subId, [rootId])
    }
    if (!packOncePerIter) placeAllRows()

    // 3) Root: centred over direct owners (above) and direct subsidiaries (below).
    const directOwners = ownersOfNode.get(rootId) ?? []
    const holdings = (ownedBy.get(rootId) ?? []).filter(c => c !== rootId)
    const rootAnchors = [...directOwners, ...holdings]
    if (rootAnchors.length > 0) {
      setTargetFromAnchor(rootId, rootAnchors)
    }
    placeAllRows()

    if (!layoutMoved) break
  }

  placeAllRows()

  // Shortcut edges (owner 2+ tiers above owned): align owner over the owned
  // column; nudge intermediate nodes sideways so bypass strokes can route
  // around them. Only nodes on the ownership *chain* between from→to are
  // "skipped over" — not unrelated co-owners on other branches.
  const skippedOver = new Set<string>()
  const shortcutTargets = new Set<string>()

  function canReachOwnedDown(start: string, end: string): boolean {
    if (start === end) return true
    const stack = [start]
    const seen = new Set<string>([start])
    while (stack.length) {
      const n = stack.pop()!
      for (const edge of layoutEdges) {
        if (edge.from !== n) continue
        if (edge.to === end) return true
        if (!seen.has(edge.to)) {
          seen.add(edge.to)
          stack.push(edge.to)
        }
      }
    }
    return false
  }

  for (const e of layoutEdges) {
    const fromL = levels.get(e.from) ?? 0
    const toL = levels.get(e.to) ?? 0
    if (fromL < toL + 2) continue
    shortcutTargets.add(e.to)
    const below = positions[e.to]
    if (!below) continue
    const belowCx = nodeCenterX(below)
    targetCx.set(e.from, belowCx)

    for (const mid of nodes) {
      if (mid.id === e.from || mid.id === e.to) continue
      const midL = levels.get(mid.id) ?? 0
      if (midL <= toL || midL >= fromL) continue
      if (
        !canReachOwnedDown(e.from, mid.id) ||
        !canReachOwnedDown(mid.id, e.to)
      ) {
        continue
      }
      skippedOver.add(mid.id)
      const side = nodeCenterX(positions[mid.id]) <= belowCx ? -1 : 1
      targetCx.set(mid.id, belowCx + side * INTERMEDIATE_SIDE_OFFSET)
    }
  }
  placeAllRows()

  if (packOncePerIter) {
    for (const n of nodes) {
      if (n.kind !== 'person') continue
      const owned = ownedBy.get(n.id) ?? []
      if (owned.length !== 1) continue
      const subId = owned[0]!
      if (skippedOver.has(subId)) continue
      const coOwners = ownersOfNode.get(subId) ?? []
      if (coOwners.length < 2) continue
      const subCx = nodeCenterX(positions[subId]!)
      for (const ownerId of coOwners) {
        targetCx.set(ownerId, subCx)
      }
      for (const child of ownedBy.get(subId) ?? []) {
        if (child === rootId) continue
        const childParents = ownersOfNode.get(child) ?? []
        if (childParents.length === 1 && childParents[0] === subId) {
          targetCx.set(child, subCx)
        }
      }
    }
    placeAllRows()
  }

  function bounds() {
    let lo = Infinity
    let hi = -Infinity
    for (const p of Object.values(positions)) {
      lo = Math.min(lo, p.x)
      hi = Math.max(hi, p.x + p.w)
    }
    return { minX: lo, maxX: hi }
  }

  // Flat owners (computed above) sit LEFT of the main subtree and are excluded
  // from the svgWidth calculation so they do not symmetrically inflate the graph.

  // Pan the laid-out graph so the root sits dead centre over the main subtree.
  // Flat owners are excluded from this bounds pass; they are placed afterward.
  const rootCx0 = nodeCenterX(positions[rootId]!)
  const mainBounds = (() => {
    let lo = Infinity
    let hi = -Infinity
    for (const [id, p] of Object.entries(positions)) {
      if (flatOwnerIds.has(id)) continue
      lo = Math.min(lo, p.x)
      hi = Math.max(hi, p.x + p.w)
    }
    if (lo === Infinity) { lo = rootCx0 - NODE_W_COMPANY / 2; hi = rootCx0 + NODE_W_COMPANY / 2 }
    return { minX: lo, maxX: hi }
  })()
  let svgWidth = Math.max(
    2 * (PADDING_X - mainBounds.minX + rootCx0),
    2 * (mainBounds.maxX - rootCx0 + PADDING_X),
    NODE_W_COMPANY + 2 * PADDING_X,
  )
  const graphCenterCx = svgWidth / 2
  const delta = graphCenterCx - rootCx0
  for (const p of Object.values(positions)) {
    p.x += delta
  }

  const rootPos = positions[rootId]!
  rootPos.x = graphCenterCx - rootPos.w / 2

  if (packOncePerIter) {
    // Tight width around the real content span. The graph may still branch
    // upward (a node with several co-owners), so the content is NOT necessarily
    // symmetric around the root — we must shift every node so the leftmost edge
    // lands at PADDING_X, otherwise right-hand branches overflow svgWidth and
    // get clipped by the SVG viewBox at any zoom. (Only re-centring the root,
    // as before, left those branches outside the viewBox.)
    const b = bounds()
    svgWidth = Math.max(
      b.maxX - b.minX + 2 * PADDING_X,
      NODE_W_COMPANY + 2 * PADDING_X,
    )
    const shift = PADDING_X - b.minX
    if (shift !== 0) {
      for (const p of Object.values(positions)) p.x += shift
    }
  }

  // Place flat owners using the now-shifted subtree positions as anchors.
  finalizeFlatRootOwnerBranchSpacing()

  // Extend svgWidth to the left only as much as flat owners need; shift all nodes
  // right by the same amount so the leftmost flat owner ends up at PADDING_X.
  if (flatOwnerIds.size > 0) {
    let flatMinX = Infinity
    for (const id of flatOwnerIds) {
      const p = positions[id]
      if (p) flatMinX = Math.min(flatMinX, p.x)
    }
    if (flatMinX < PADDING_X) {
      const leftExt = Math.ceil(PADDING_X - flatMinX)
      for (const p of Object.values(positions)) p.x += leftExt
      svgWidth += leftExt
    }
  }

  const svgHeight = finalizeVerticalViewport(
    positions,
    layoutEdges,
    rootId,
    viewport,
  )

  return { positions, svgWidth, svgHeight, minLevel, maxLevel }
}
